import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    ModalActionsComponent,
    ModalComponent,
    PasslyCheckbox,
    PasslyInfiniteScroll,
    PasslyTextField
} from '@app/components';

import { useTypedTranslation } from '@app/hooks';
import { Box, CircularProgress, FormControl } from '@material-ui/core';
import { vaultsService } from '@app/services';
import { getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { genericErrorToast, infiniteScrollItemsFetchLimit, useDebounce } from '@app/helpers';
import { IVaultsDisplay } from '@app/models';
import ConfirmationPopupBodyComponent from '@app/pages/password/components/confirmationPopupBody';

interface IRowProps {
    displayName: string;
    checked: boolean;
    id: number;
    orgName: string;
}

export const ChooseVaultsForExportDialog: React.SFC = () => {
    const dispatch = useDispatch();
    const [selectAllCheckbox, setSelectAllCheckbox] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [displayText, setDisplayText] = useState('');
    const [rows, setRows] = useState<IRowProps[]>([]);
    const [resetScrollCount, setResetScrollCount] = useState(0);

    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const { translation } = useTypedTranslation();
    const isButtonDisabled = !rows.filter((el: IRowProps) => {
        return el.checked;
    }).length;
    const debouncedValue = useDebounce(displayText, 500);

    const handleFilteredList = (e: any) => {
        const value = e.target.value;

        setDisplayText(value);
    };

    const onCloseConfirm = () => {
        setShowConfirm(false);
    };

    const showConfirmDialog = () => {
        setShowConfirm(true);
    };

    const handleError = (error: any) => {
        setDataLoading(false);

        genericErrorToast(error);
    };

    const handleSuccess = (response: any) => {
        const count = response.data.count;

        if (count !== undefined) {
            setTotalCount(count);
        }

        const results: IVaultsDisplay[] = response.data.items;

        if (results) {
            let newRows = results.map((row: any) => {
                    const data: any = {
                        checked: false,
                        id: row.id,
                        displayName: row.displayName,
                        orgName: row.orgName
                    };

                    return data;
                })
            ;

            if (results.length === 0) {
                setRows([]);
                setSelectAllCheckbox(false);
            } else if (count !== undefined && !rows.length) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setDataLoading(false);
    };

    const handleExportVaults = () => {
        const selectedItems = rows
            .filter((el: IRowProps) => {
                return el.checked;
            })
            .map((item) => item.id);

        dispatch(getLoaderBtn(true));
        vaultsService.exportVaults(selectedItems).then(handleExportSuccess).catch(handleExportError);
        onCloseConfirm();
    };

    const handleCancel = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const handleExportSuccess = () => {
        dispatch(getLoaderBtn(true));
        handleCancel();
        dispatch(handleSetSnackbar({ show: true, message: translation('EXPORT_VAULTS_SUCCESS'), severity: 'success' }));
    };

    const handleExportError = () => {
        dispatch(getLoaderBtn(true));
        handleCancel();
        dispatch(handleSetSnackbar({ show: true, message: translation('EXPORT_VAULTS_ERROR'), severity: 'error' }));
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const indexOfItem: number = rows.findIndex((item: IRowProps) => item.id === e.target.name);

        if (indexOfItem !== -1) {
            let newRows: IRowProps[] = [...rows];

            newRows[indexOfItem].checked = checked;
            setRows(newRows);
        }
    };

    const updateAllCheckboxes = (checked: boolean, e: any) => {
        if (rows.length > 0) {
            let newRows: IRowProps[] = [...rows];

            setSelectAllCheckbox(checked);

            if (e.target) {
                newRows.forEach((newRow) => {
                    newRow.checked = checked;
                });

                setRows(newRows);
            }
        }
    };

    const resetAllCheckboxes = () => {
        if (rows.length > 0) {
            let newRows: IRowProps[] = [...rows];

            setSelectAllCheckbox(false);
            newRows.forEach((newRow) => {
                newRow.checked = false;
            });
            setRows(newRows);
        }
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        setDataLoading(true);
        vaultsService.getAvailableVaultsForExport(
            infiniteScrollItemsFetchLimit,
            skipCount,
            handleError,
            handleSuccess,
            debouncedValue
        );
    };

    useEffect(() => {
        if (!dataLoading) {
            resetAllCheckboxes();
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [debouncedValue]);

    useEffect(() => {
        setRows([]);
    }, [displayText]);

    return (
        <>
                <div className='modal__identities-list' tabIndex={0}>
                    <div className='modal__identities-list--filter'>
                        <FormControl>
                            <PasslyTextField fullWidth id='modal-identities-list-filter' label={'FILTER_BY'} value={displayText} onChange={handleFilteredList} autoFocus showSearchIcon={true} />
                        </FormControl>
                        <PasslyCheckbox
                            name='selectAllIdentityCheckbox'
                            checked={selectAllCheckbox}
                            onChange={updateAllCheckboxes}
                            label={translation('SELECT_ALL')}
                        />
                    </div>
                    <PasslyInfiniteScroll
                        totalCount={totalCount ?? 0}
                        currentCount={rows.length}
                        onFetch={fetchMoreData}
                        resetLocal={resetScrollCount}
                    >
                        <>
                            {rows.length
                                ? rows.map((item: any) => (
                                      <div className='modal__identities-list--row modal__form--checkbox' key={item.id}>
                                          <PasslyCheckbox
                                              name={item.id}
                                              checked={item.checked}
                                              onChange={onCheckboxChange}
                                              label=''
                                          />
                                          <Box ml={1}>{item.displayName}</Box>
                                          <div className='modal__identities-list--org'>({item.orgName})</div>
                                      </div>
                                  ))
                                : null}
                        </>
                    </PasslyInfiniteScroll>
                    {!rows.length && dataLoading ? (
                        <CircularProgress size={64} className='modal__identities-list-loader' />
                    ) : !rows.length ? (
                        <p className='modal__identities-list--no-data'>{translation('EXPORT_NO_VAULTS_TO_EXPORT')}</p>
                    ) : null}
            </div>
            <ModalActionsComponent
                primaryBtn={translation('EXPORT_PASSWORDS')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={showConfirmDialog}
                onClickSecondary={handleCancel}
                disabled={isButtonDisabled}
                id='addRoles'
            />
            <ModalComponent
                headerTitle={translation('EXPORT_VAULTS_CONFIRMATION_TITLE')}
                localModalSwitcher={showConfirm}
                modalSwitcherCallback={onCloseConfirm}
                form={<ConfirmationPopupBodyComponent onCancelClick={onCloseConfirm} confirmCallback={handleExportVaults} />}
            />
        </>
    );
};
